import React from 'react';
import { Tooltip } from '@mui/material';
import { StatusChip } from '../ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  BrandAuthorization,
  VERIFICATION_TAG_META,
  verificationTagOf,
} from '../../utils/brandAuthorization';

type TFn = ReturnType<typeof useAppTranslation>['t'];

/** Label and tooltip for a record's tag; null when the claim needs no proof. */
export function brandAuthorizationTagCopy(
  t: TFn,
  record: BrandAuthorization,
): { label: string; help: string; tone: (typeof VERIFICATION_TAG_META)[keyof typeof VERIFICATION_TAG_META]['tone'] } | null {
  const tag = verificationTagOf(record);
  if (tag.kind === 'not_required') return null;
  const meta = VERIFICATION_TAG_META[tag.kind];
  const brand = tag.listBrandName || record.brandName;
  const dealer = tag.match?.dealerName || record.brandName;
  const labelKey = tag.kind === 'list_match' && tag.match?.reason === 'partial_name' ? 'listMatchPartial' : meta.localeKey;
  const helpKey = tag.kind === 'list_match' && tag.match?.reason === 'stale_list' ? 'listMatchStale' : meta.localeKey;
  return {
    label: t(`store.brandAuthorization.tag.${labelKey}`, { brand }),
    help: t(`store.brandAuthorization.tagHelp.${helpKey}`, { brand, dealer }),
    tone: meta.tone,
  };
}

/** The one status chip a brand claim shows, with what it means on hover. */
const BrandAuthorizationTagChip: React.FC<{ record: BrandAuthorization; size?: 'small' | 'medium' }> = ({
  record,
  size = 'small',
}) => {
  const { t } = useAppTranslation();
  const copy = brandAuthorizationTagCopy(t, record);
  if (!copy) return null;
  return (
    <Tooltip title={copy.help}>
      <span>
        <StatusChip label={copy.label} tone={copy.tone} size={size} sx={{ height: 24, fontSize: '0.7rem' }} />
      </span>
    </Tooltip>
  );
};

export default BrandAuthorizationTagChip;
